import React, { useEffect, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import { getDocketEntries } from "../services/docketEntryService";
import { getBranches } from "../services/branchService";
import { useSelector } from "react-redux";
import { AlertCircle, Package, Clock, MapPin, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import moment from "moment";

const StuckDockets = () => {
  const { user } = useSelector((state) => state.user);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [stuckDocketsList, setStuckDocketsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetBranches = async () => {
    try {
      const response = await getBranches();
      if (response?.status === 200) {
        setBranches(response?.data);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const calculateStuckDockets = async () => {
    setLoading(true);
    try {
      // Pass selectedBranch to get dockets for that specific branch
      const response = await getDocketEntries(selectedBranch);
      if (response?.status === 200) {
        const dockets = response?.data || [];

        // Logic: if in history array there is object with status "IN_TRANSIT",
        // then it is NOT treated as stuck.
        const stuckDockets = dockets.filter((docket) => {
          // Ignore dockets without a history key or if it is empty
          if (!docket.history || !Array.isArray(docket.history) || docket.history.length === 0) {
            return false;
          }

          // Check if docket matches selected branch
          const matchesBranch = !selectedBranch || 
            (docket.branchCode && docket.branchCode.trim().toLowerCase() === selectedBranch.trim().toLowerCase());
          if (!matchesBranch) return false;

          const hasCreated = docket.history.some((h) => h.status === "CREATED");
          const hasInTransit = docket.history.some((h) => h.status === "IN_TRANSIT");

          // Stuck ONLY if it has CREATED and DOES NOT have IN_TRANSIT
          return hasCreated && !hasInTransit;
        });

        // Sort by most recent booking date if available
        stuckDockets.sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate));

        setStuckDocketsList(stuckDockets);
      }
    } catch (error) {
      console.error("Error fetching docket list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetBranches();
    if (user?.role === "EMPLOYEE") {
      setSelectedBranch(user?.branch?.code || "");
    }
  }, [user]);

  useEffect(() => {
    calculateStuckDockets();
  }, [selectedBranch]);

  return (
    <div className="p-4 bg-slate-50 min-h-full font-sans">
      <div className="mb-8">
        <SectionHeader
          title="Stuck Dockets Tracker"
          subTitle="Monitor and resolve dockets that haven't moved to IN_TRANSIT status"
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-8">
        {/* Left Side: Filter and Summary */}
        <div className="w-full lg:w-1/3 flex flex-col gap-6">
          {/* Filter Card */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="text-sm font-bold tracking-wide text-gray-500 uppercase mb-4">
              Filter Operations
            </h3>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Branch
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-blue-500" />
              </div>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50/50 hover:bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-800"
              >
                <option value="">All Branches</option>
                {branches?.map((b) => (
                  <option key={b._id} value={b.branchCode}>
                    {b.branchName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-br from-rose-500 to-red-600 p-8 rounded-2xl shadow-[0_8px_30px_rgb(225,29,72,0.2)] text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6">
                <AlertCircle className="text-white" size={26} />
              </div>
              <h2 className="text-rose-100 font-medium mb-1">Total Stuck Dockets</h2>
              <div className="flex items-baseline gap-2">
                {loading ? (
                  <div className="h-12 w-16 bg-white/20 rounded animate-pulse"></div>
                ) : (
                  <span className="text-5xl font-black tracking-tight">
                    {stuckDocketsList.length}
                  </span>
                )}
                <span className="text-rose-200 font-medium text-lg">dockets</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Stuck Dockets List */}
        <div className="w-full lg:w-2/3">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-full">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Package className="text-blue-500" size={20} />
                Stuck Dockets Details
              </h3>
              {!loading && stuckDocketsList.length > 0 && (
                <span className="px-3 py-1 bg-rose-50 text-rose-600 text-xs font-bold rounded-full border border-rose-100">
                  {stuckDocketsList.length} Action Required
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="font-medium">Loading dockets data...</p>
              </div>
            ) : stuckDocketsList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-emerald-50/50 rounded-xl border border-dashed border-emerald-200">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-4 shadow-sm">
                  <Package size={32} />
                </div>
                <h4 className="text-lg font-bold text-emerald-800 mb-1">All Clear!</h4>
                <p className="text-sm text-emerald-600/80 font-medium">No stuck dockets found for the selected branch.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-max h-[500px] overflow-y-auto pr-2" style={{ scrollbarWidth: "thin" }}>
                {stuckDocketsList.map((docket, idx) => {
                  const latestHistory = docket.history && docket.history.length > 0 
                    ? docket.history[docket.history.length - 1] 
                    : null;

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      key={docket._id || idx}
                      className="group p-5 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-blue-100 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-rose-500 group-hover:bg-blue-500 transition-colors"></div>
                      <div className="flex justify-between items-start mb-3">
                        <span className="px-2.5 py-1 bg-white border border-gray-200 shadow-sm rounded-md text-sm font-bold text-gray-800 tracking-wide">
                          {docket.docketNumber}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-rose-100 text-rose-700 rounded">
                          Stuck
                        </span>
                      </div>
                      
                      <div className="space-y-2 mt-4">
                        <div className="flex items-center text-sm text-gray-600 font-medium">
                          <MapPin size={15} className="mr-2 text-gray-400" />
                          Branch: <span className="ml-1 text-gray-900">{docket.branchCode || "N/A"}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 font-medium">
                          <Calendar size={15} className="mr-2 text-gray-400" />
                          Booked: <span className="ml-1 text-gray-900">{docket.bookingDate ? moment(docket.bookingDate).format("DD-MM-YYYY") : "N/A"}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 font-medium">
                          <Clock size={15} className="mr-2 text-gray-400" />
                          Last Status: 
                          <span className="ml-1 px-2 py-0.5 bg-gray-200/50 text-gray-700 rounded text-xs font-bold">
                            {latestHistory?.status || "UNKNOWN"}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StuckDockets;
